import { useState, useEffect, useCallback } from "react";
import { useInterval } from "../../../components/src/common/Utils";
import { BankIdData, BidStatus, BidHintCode } from "../types/bankId";
import { initiateBankIdAuth, collectBankIdAuth, resetBankIdData, createInitialBankIdData } from "../utils/authUtils";

interface UseBankIdAuthProps {
    loggedIn: boolean;
    usePassword: boolean;
    onLoginSuccess: (loginData: any) => void;
}

export const useBankIdAuth = ({ loggedIn, usePassword, onLoginSuccess }: UseBankIdAuthProps) => {
    const [bankIdData, setBankIdData] = useState<BankIdData>(createInitialBankIdData);

    const handleBankIdSuccess = useCallback(
        (data: any) => {
            const expireDate = new Date();
            expireDate.setSeconds(expireDate.getSeconds() + Number(data.expires_in));
            const expires = expireDate.toISOString();

            onLoginSuccess({
                expires,
                loggedIn: true,
                token: data.access_token,
                username: data.user_display_name,
                userId: data.user_id || "",
                statusMessage: "",
                errorMessage: "",
                acceptedTerms: data.accepted_terms
            });

            setBankIdData(resetBankIdData());
            localStorage.setItem("orderRef", "");
        },
        [onLoginSuccess]
    );

    const handleBankIdFailure = useCallback((data: any) => {
        setBankIdData((prev) => ({
            ...prev,
            status: BidStatus.failed,
            hintCode: data.hintCode,
            qrCode: data.qrCode,
            orderRef: ""
        }));
        localStorage.setItem("orderRef", "");
    }, []);

    const handleBankIdUpdate = useCallback((data: any) => {
        setBankIdData((prev) => ({
            ...prev,
            status: data.status,
            hintCode: data.hintCode,
            qrCode: data.qrCode
        }));
    }, []);

    const startBankIdAuth = useCallback(async () => {
        const authData = await initiateBankIdAuth();
        if (authData?.orderRef) {
            localStorage.setItem("orderRef", authData.orderRef);
            setBankIdData({
                autoStartToken: authData.autoStartToken,
                qrStartToken: "",
                orderRef: authData.orderRef,
                status: BidStatus.pending,
                hintCode: BidHintCode.outstandingTransaction,
                qrCode: authData.qrCode
            });
        }
    }, []);

    const collectBankIdStatus = useCallback(async () => {
        // Guard against polling with empty orderRef (race condition on mobile reload)
        if (!bankIdData.orderRef) {
            return;
        }
        const data = await collectBankIdAuth(bankIdData.orderRef);
        if (data) {
            if (data.status === BidStatus.complete) {
                handleBankIdSuccess(data);
            } else if (data.status === BidStatus.failed) {
                handleBankIdFailure(data);
            } else {
                handleBankIdUpdate(data);
            }
        }
    }, [bankIdData.orderRef, handleBankIdSuccess, handleBankIdFailure, handleBankIdUpdate]);

    const restartBankIdAuth = useCallback(() => {
        setBankIdData(resetBankIdData());
    }, []);

    const openBankIdApp = useCallback(() => {
        if (bankIdData?.autoStartToken) {
            // Use the BankID universal/app link rather than the bankid:// custom scheme.
            // Modern iOS Safari and Android Chrome block custom URL schemes triggered from
            // JavaScript navigation, but https://app.bankid.com/ is registered as a universal
            // link that reliably opens the BankID app on both platforms.
            // See BankID Relying Party Guidelines, section 3 "Launching".
            const url = `https://app.bankid.com/?autostarttoken=${bankIdData.autoStartToken}&redirect=${encodeURIComponent(window.location.href)}`;
            window.location.href = url;
        }
    }, [bankIdData?.autoStartToken]);

    // Start BankID auth when conditions are met
    useEffect(() => {
        if (!loggedIn && !usePassword && bankIdData.status === BidStatus.start) {
            startBankIdAuth();
        }
    }, [loggedIn, usePassword, bankIdData.status, startBankIdAuth]);

    // Poll for BankID status updates
    // Only poll if we have a valid orderRef to prevent race conditions on mobile reload
    useInterval(
        collectBankIdStatus,
        !loggedIn && !usePassword && bankIdData.status === BidStatus.pending && bankIdData.orderRef ? 1100 : null
    );

    return {
        bankIdData,
        restartBankIdAuth,
        openBankIdApp
    };
};
